import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '../../data/collections';
import { site } from '../../data/site';

const DURATION = 7000;

/**
 * Editorial hero: full-bleed image, type anchored bottom-left behind a mask,
 * and a vertical slide index on the right with a filling progress rule.
 */
export default function Hero() {
  const [active, setActive] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (!paused.current) setActive((i) => (i + 1) % heroSlides.length);
    }, DURATION);
    return () => clearInterval(id);
  }, []);

  const slide = heroSlides[active];

  return (
    <section
      className="relative flex min-h-[calc(100svh-76px)] flex-col overflow-hidden bg-royal-900 lg:min-h-[calc(100svh-96px)]"
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
    >
      {/* Image stack */}
      <div className="absolute inset-0">
        {heroSlides.map((s, i) => (
          <div
            key={s.img}
            className={`absolute inset-0 transition-opacity duration-[1800ms] ease-luxe ${
              i === active ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden={i !== active}
          >
            <img
              src={s.img}
              alt=""
              loading={i === 0 ? 'eager' : 'lazy'}
              className="h-full w-full object-cover object-[center_20%] transition-transform duration-[9000ms] ease-out"
              style={{ transform: i === active ? 'scale(1.06)' : 'scale(1)' }}
            />
          </div>
        ))}

        {/* Scrims — weighted left so the right side keeps its detail */}
        <div className="absolute inset-0 bg-gradient-to-r from-royal-900/90 via-royal-900/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-royal-900 via-royal-900/15 to-royal-900/55" />
      </div>

      {/* Content */}
      <div className="shell relative flex flex-1 flex-col justify-end pb-14 pt-20 lg:pb-20 lg:pt-24">
        <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-16">
          {/* Type */}
          <div key={active} className="min-w-0 max-w-3xl">
            <span className="rise">
              <span className="inline-flex items-center gap-4 text-[12px] font-medium uppercase tracking-luxe text-gold-200">
                <span className="h-px w-10 bg-gold-300/60" />
                {slide.kicker}
              </span>
            </span>

            <h1 className="mt-6 font-display text-[2.7rem] leading-[0.98] text-cream sm:text-[4rem] lg:text-[5.2rem]">
              {slide.title.split('\n').map((line, i) => (
                <span key={line} className={`rise rise-d${i + 1}`}>
                  <span>{line}</span>
                </span>
              ))}
            </h1>

            <div className="rise rise-d3 mt-7">
              <span>
                <p className="max-w-xl text-[15px] font-light leading-[1.75] text-cream/70 sm:text-[16.5px]">
                  {slide.copy}
                </p>
              </span>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-4 sm:gap-x-10">
              <Link to={slide.to} className="btn-line-light">
                <span className="btn-line-rule" />
                {slide.cta}
              </Link>
              <Link
                to="/collections"
                className="text-[12px] font-medium uppercase tracking-luxe text-cream/85 transition-colors duration-500 hover:text-gold-200"
              >
                All collections
              </Link>
            </div>
          </div>

          {/* Vertical index */}
          <div className="hidden shrink-0 lg:block">
            <ul className="space-y-5">
              {heroSlides.map((s, i) => (
                <li key={s.kicker}>
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    aria-current={i === active}
                    className="group flex items-center gap-4 text-right"
                  >
                    <span
                      className={`text-[12px] tracking-wider2 transition-colors duration-500 ${
                        i === active ? 'text-gold' : 'text-cream/60 group-hover:text-cream/80'
                      }`}
                    >
                      0{i + 1}
                    </span>
                    <span className="relative block h-px w-16 bg-cream/15">
                      <span
                        className={`absolute inset-y-0 left-0 bg-gold transition-all ease-linear ${
                          i === active ? 'w-full' : 'w-0'
                        }`}
                        style={{ transitionDuration: i === active ? `${DURATION}ms` : '350ms' }}
                      />
                    </span>
                    <span
                      className={`w-[132px] text-left text-[12px] uppercase tracking-wider2 transition-colors duration-500 ${
                        i === active ? 'text-cream' : 'text-cream/60 group-hover:text-cream/80'
                      }`}
                    >
                      {s.kicker}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Baseline */}
      <div className="relative border-t border-cream/10">
        <div className="shell flex items-center justify-between py-4">
          <p className="text-[11.5px] uppercase tracking-luxe text-cream/60">
            {site.positioning}
          </p>
          <p className="hidden text-[11.5px] uppercase tracking-luxe text-cream/60 sm:block">
            {site.address.city}, {site.address.state}
          </p>
          <p className="text-[11.5px] uppercase tracking-luxe text-gold/60">Est. {site.since}</p>
        </div>
      </div>
    </section>
  );
}
